import { importProvidersFrom } from '@angular/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import {
  RouterCancel,
  RouterDataResolved,
  RouterError,
  RouterNavigated,
  RouterNavigation,
  RouterRequest,
} from '@ngxs/router-plugin';
import { NgxsDevelopmentModule } from '@ngxs/store';

import {
  provideAsiStats,
  type AsiStatsConfig,
} from '@cosmos/analytics/asi-stats';
import { provideConfig } from '@cosmos/config';
import {
  withFlagsSelect,
  withFlagsToolbarInformation,
  withFlagsTranslations,
  withListenersDebugger,
  withSignalsDebugger,
  withStatsDebugging,
} from '@cosmos/dev-toolbar';
import {
  ExplicitFeatureFlagsService,
  provideFeatureFlagsService,
} from '@cosmos/feature-flags';
import { provideFeatureFlagsToolbarInitializer } from '@cosmos/feature-flags-toolbar';
import { provideTranslations } from '@cosmos/util-translations';
import { withPresentationTemplateSelect } from '@customer-portal/feature-flags-select-template';

import type { EnvironmentConfig } from '../app/core/types/environment-config';

import { configOverrides } from './dev-overrides';
import { featureFlagsToolbarOptions } from './features';

// dev-asicentral.com
const ASICENTRAL_DOMAIN =
  configOverrides.asiCentralDomain ||
  process.env.ASICENTRAL_DOMAIN ||
  `dev-asicentral.com`;

const ASI_STATS_CONFIG: AsiStatsConfig | undefined =
  configOverrides.asiStatsEnabled
    ? {
        url: `https://stash.${ASICENTRAL_DOMAIN}`,
        buffer: {
          timeSpan: 5000,
          maxSize: 5,
        },
        retry: {
          count: 3,
          delay: 2000,
        },
      }
    : undefined;

const production = false;

const toolbarFeatures = [
  withFlagsSelect(),
  withFlagsTranslations(),
  withStatsDebugging(),
  withPresentationTemplateSelect(),
  withFlagsToolbarInformation({
    url: `/asi/ngx-esp/commit/${process.env['GIT_SHA']}`,
  }),
];

if (ngDevMode) {
  toolbarFeatures.push(withSignalsDebugger(), withListenersDebugger());
}

export const environment: EnvironmentConfig = {
  production,

  imports: [],

  providers: [
    importProvidersFrom(
      NgxsDevelopmentModule.forRoot({
        warnOnUnhandledActions: {
          // These actions are dispatched by the router plugin, but we don't
          // handle them explicitly in any state. We're safe to ignore them
          // from being warned.
          ignore: [
            RouterRequest,
            RouterNavigation,
            RouterCancel,
            RouterError,
            RouterDataResolved,
            RouterNavigated,
          ],
        },
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        filter(action, state) {
          return false;
        },
        disabled: false,
      })
    ),

    provideTranslations({
      production,
      appName: 'customer-portal',
    }),

    provideFeatureFlagsService(ExplicitFeatureFlagsService),
    provideConfig({
      smartlinkApiUrl: `https://api.${ASICENTRAL_DOMAIN}/v1`,
      espServiceApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api/esp`,

      venusApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api`,
      vulcanApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api`,
      marsApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api`,
      janusApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api`,
      siriusApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api/esp`,
      neptunePublicApiUrl: `https://services.${ASICENTRAL_DOMAIN}/neptune-public/api`,
      sednaApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api/sedna`,
      rosettaApiUrl: `https://services.${ASICENTRAL_DOMAIN}/mars/api/rosetta`,
      pdfConverter: `https://pdfconverter.${ASICENTRAL_DOMAIN}/1.3/api/print`,
      storefrontImgixUrl: 'https://asi-621231177.imgix.net',

      commonMediaUrl: `https://commonmedia.${ASICENTRAL_DOMAIN}`,

      myPromoOfficeDomain:
        configOverrides.myPromoOfficeDomain ||
        process.env.MYPROMOOFFICE_DOMAIN ||
        'dev-mypromooffice.com',

      asiCentralDomain: ASICENTRAL_DOMAIN,

      // Analytics
      asiStats: ASI_STATS_CONFIG,
    }),

    provideFeatureFlagsToolbarInitializer(
      featureFlagsToolbarOptions,
      ...toolbarFeatures
    ),
    ...(ASI_STATS_CONFIG ? [provideAsiStats()] : []),
  ],
};
